import React, { useMemo } from 'react';
import { Box, Typography, TextField, Button, InputAdornment } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import { CHART_INK, formatMonthLabel } from './chartUtils';

const buildChartData = (monthlyData, minutesSavedPerUnit) => {
    let cumulative = 0;
    return monthlyData.map((m) => {
        const hoursSaved = (m.count * minutesSavedPerUnit) / 60;
        cumulative += hoursSaved;
        return {
            label: formatMonthLabel(m.year, m.month),
            hoursSaved: Math.round(hoursSaved * 10) / 10,
            cumulativeHoursSaved: Math.round(cumulative * 10) / 10,
        };
    });
};

const SavingsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ bgcolor: '#fff', border: '1px solid #e1e0d9', borderRadius: 1, p: 1.2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Typography variant="caption" fontWeight={600}>{label}</Typography>
            {payload.map((entry) => (
                <Typography key={entry.dataKey} variant="caption" display="block" sx={{ color: entry.color }}>
                    {entry.name}: {entry.value.toLocaleString('he-IL')} שעות
                </Typography>
            ))}
        </Box>
    );
};

// Generic "hours saved" card: an editable minutes-per-unit setting (persisted
// server-side by the caller) applied to a monthly count, shown as a monthly
// bar chart plus a cumulative line chart. `monthlyData` items: `{year, month, count}`.
const SavingsChart = ({
    monthlyData,
    color,
    minutesSavedPerUnit,
    minutesInput,
    onMinutesInputChange,
    onSaveMinutes,
    saving,
    expanded,
    minutesFieldLabel = "דקות שנחסכות ליחידה",
    monthlyLabel = 'שעות שנחסכו בחודש',
    cumulativeLabel = 'חיסכון מצטבר (שעות)',
}) => {
    const data = useMemo(
        () => buildChartData(monthlyData || [], minutesSavedPerUnit),
        [monthlyData, minutesSavedPerUnit],
    );

    const panelHeight = expanded ? 220 : 110;
    const minutesDirty = Number(minutesInput) !== minutesSavedPerUnit;

    return (
        <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5} onClick={(e) => e.stopPropagation()}>
                <TextField
                    label={minutesFieldLabel}
                    type="number"
                    size="small"
                    value={minutesInput}
                    onChange={(e) => onMinutesInputChange(e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">דק'</InputAdornment>, inputProps: { min: 0 } }}
                    sx={{ width: 210 }}
                />
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    disabled={!minutesDirty || saving}
                    onClick={onSaveMinutes}
                >
                    {saving ? 'שומר...' : 'שמירה'}
                </Button>
            </Box>

            {!data.length ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>אין נתונים לטווח שנבחר</Typography>
            ) : (
                <>
                    <Typography variant="caption" color="text.secondary">{monthlyLabel}</Typography>
                    <ResponsiveContainer width="100%" height={panelHeight}>
                        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
                            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} />
                            <Tooltip content={<SavingsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                            <Bar dataKey="hoursSaved" name="שעות שנחסכו" fill={color} radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ResponsiveContainer>

                    <Typography variant="caption" color="text.secondary">{cumulativeLabel}</Typography>
                    <ResponsiveContainer width="100%" height={panelHeight}>
                        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} />
                            <Tooltip content={<SavingsTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="cumulativeHoursSaved"
                                name="חיסכון מצטבר"
                                stroke={color}
                                strokeWidth={2}
                                dot={{ r: 3, fill: color }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </>
            )}
        </Box>
    );
};

export default SavingsChart;
